<script setup lang="ts">
import type { FeedPage, ShortVideo } from '~/composables/shorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import {
	breakpointsTailwind,
	useBreakpoints,
	useWindowSize,
	watchDebounced
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import {
	SHORTS_LISTING_SURFACE_CONTRACT,
	useFeedSession,
	useFetchShorts,
	useOpenShortsFeed,
	usePropertyIntentPrefetch
} from '~/composables/shorts';
import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';
import { withImagesEveryForced } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { resolveShortsLocationStatesForLabels } from '~/composables/useShortsFilters/resolveShortsLocationStates';
import { useShortsFilters } from '~/composables/useShortsFilters/useShortsFilters';
import { useShortsListingInfiniteScroll } from '~/composables/useShortsListingInfiniteScroll';
import { useShortsScrollCompact } from '~/composables/useShortsScrollCompact';
import {
	prefetchReelPosterOnIntent,
	useVideoCdnPreconnect
} from '~/composables/useVideoWarmup';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cn, removeEmptyValues } from '~/utils';

import { ShortsFiltersBar } from '~/components/Filters/ShortsFiltersBar';
import { shortsListingPaddingX } from '~/components/Filters/ShortsFiltersBar/shortsHorizontalPadding';
import { VideoCardHomeFeed } from '~/components/VideoCard';

import PageEmptyState from './PageEmptyState.vue';
import PageSkeleton from './PageSkeleton.vue';

useShortsScrollCompact();

const { height: windowHeight } = useWindowSize();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');
const playerRootMargin = computed(() => {
	if (isMobile.value) return '150px';
	const height = windowHeight.value;
	return height > 0 ? `${Math.round(height * 0.6)}px` : '300px';
});

// On slow networks, load fewer images eagerly so video manifests/segments
// get bandwidth first. preload=0 means no <link rel="preload"> tags at all.
const savedBandwidthEstimate = ref<number | null | undefined>(undefined);

const imgEagerCount = computed(() => {
	const bps = savedBandwidthEstimate.value;
	if (bps === undefined) return 6;
	if (bps === null || bps >= 4_000_000) return 12; // fast: preload aggressively
	if (bps >= 2_000_000) return 6; // medium: moderate
	return 2; // slow: bare minimum
});

const imgPreloadCount = computed(() => {
	const bps = savedBandwidthEstimate.value;
	if (bps === undefined) return 2;
	if (bps === null || bps >= 4_000_000) return 4;
	if (bps >= 2_000_000) return 2;
	return 0; // slow: no preload hints at all
});

const route = useRoute();
const { trackShortsEmptyState, trackCtaViewMoreClicked } = useEvents();
const EMPTY_STATE_SECTION_NAME = 'home feed shorts';

const props = defineProps<{
	initialPage?: FeedPage;
}>();

// SSR head hint: warm the video CDN connection (DNS+TCP+TLS, ~60ms) so the
// first autoplaying card's manifest request skips the handshake. Derived from
// the first SSR item so a CDN domain change never leaves a stale hardcode.
useVideoCdnPreconnect(
	computed(
		() => props.initialPage?.items.find(item => item.kind === 'video')?.videoUrl
	)
);

const { fetchVideos } = useFetchShorts();
const {
	toShortsListingFeaturedVideosFilters,
	clearFilters,
	cities,
	regions,
	macroregions,
	states,
	propertyTypes,
	minAskingPrice,
	maxAskingPrice,
	minArea,
	maxArea
} = useShortsFilters();

const skipNextFilterReset = ref(false);
let clearFiltersSkipTimeout: ReturnType<typeof setTimeout> | null = null;

const PAGE_SIZE = SHORTS_LISTING_SURFACE_CONTRACT.perPage;
const LOAD_MORE_OFFSET_PX = 400;
const LOAD_MORE_ROOT_MARGIN = `${LOAD_MORE_OFFSET_PX}px 0px`;

const loadMoreRef = ref<HTMLElement | null>(null);

/**
 * A grade não é dona da lista: ela só diz quais filtros quer e quando carregar
 * mais. Itens, dedupe, paginação, contadores e controle de corrida são do
 * `useFeedSession`.
 */
const feedSession = useFeedSession({
	perPage: PAGE_SIZE,
	fetchOptions: SHORTS_LISTING_SURFACE_CONTRACT.fetchOptions,
	initialPage: props.initialPage
});

const {
	items: videos,
	hasMore,
	isLoading,
	isLoadingMore: isInfiniteLoading,
	isLoaded,
	isStopped: blockListingFetches
} = feedSession;

/**
 * A prévia do sidebar mostra a contagem de um filtro que o usuário ainda não
 * aplicou. Fica fora da lista para não fingir que a lista carregada tem esse
 * tamanho; some no próximo carregamento real.
 */
const previewTotalAvailable = ref<number | null>(null);
const totalAvailable = computed(
	() => previewTotalAvailable.value ?? feedSession.totalAvailable.value
);

onBeforeRouteLeave(() => {
	feedSession.stop();
});

function scrollShortsFeedToTop(): void {
	if (!import.meta.client) return;

	window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}

async function reloadVideos(): Promise<void> {
	scrollShortsFeedToTop();
	previewTotalAvailable.value = null;
	resetLoadMoreCycle();
	feedSession.reset();

	await feedSession.load(toShortsListingFeaturedVideosFilters.value);
}

const { resetLoadMoreCycle } = useShortsListingInfiniteScroll({
	loadMoreRef,
	hasMore,
	isLoading,
	isInfiniteLoading,
	blockListingFetches,
	onLoadMore: feedSession.loadMore,
	rootMargin: LOAD_MORE_ROOT_MARGIN
});

onMounted(() => {
	savedBandwidthEstimate.value = getSavedBandwidthEstimate();

	if (!videos.value.length) {
		void reloadVideos();
	}
});

const unwatchFilters = watchDebounced(
	toShortsListingFeaturedVideosFilters,
	() => {
		if (blockListingFetches.value) {
			return;
		}

		if (skipNextFilterReset.value) {
			skipNextFilterReset.value = false;
			return;
		}

		void reloadVideos();
	},
	{ debounce: 150 }
);

const currentFilters = computed(() => ({
	cities: cities.value,
	regions: regions.value,
	macroregions: macroregions.value,
	states: states.value,
	propertyTypes: propertyTypes.value,
	minAskingPrice: minAskingPrice.value ?? undefined,
	maxAskingPrice: maxAskingPrice.value ?? undefined,
	minArea: minArea.value ?? undefined,
	maxArea: maxArea.value ?? undefined
}));

function resolveClickedVideo(
	commercialId: string
): { item: ShortVideo; index: number } | null {
	const currentIndex = videos.value.findIndex(
		video => video.property.commercialId === commercialId
	);
	if (currentIndex >= 0) {
		return { item: videos.value[currentIndex]!, index: currentIndex };
	}

	return null;
}

// Sinais de intencao da grade, iguais aos das outras superficies: 2s de
// preview assistido no mobile, hover sustentado no desktop.
const { prefetchPropertyOnIntent } = usePropertyIntentPrefetch();
const { openShortsFeed } = useOpenShortsFeed();
const HOVER_INTENT_MS = 150;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;

function warmOnIntent(index: number, requireSpeculation: boolean): void {
	const item = videos.value[index];
	if (!item) return;
	if (requireSpeculation && !canSpeculate()) return;
	prefetchPropertyOnIntent(item.property.commercialId);
	if (canSpeculate()) prefetchReelPosterOnIntent(item.thumbnailUrl);
}

function handleVideoCardEngaged(index: number): void {
	warmOnIntent(index, false);
}

function handleVideoCardHoverStart(index: number): void {
	clearTimeout(hoverTimer);
	hoverTimer = setTimeout(() => warmOnIntent(index, true), HOVER_INTENT_MS);
}

function handleVideoCardHoverEnd(): void {
	clearTimeout(hoverTimer);
}

onUnmounted(() => clearTimeout(hoverTimer));

function handleVideoCardClick(commercialId: string): void {
	const clickedVideo = resolveClickedVideo(commercialId);
	if (!clickedVideo) return;

	openShortsFeed({
		at: commercialId,
		page: feedSession.snapshot(clickedVideo.index)
	});
}

function handleClearFilters(): void {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Shorts',
		[MixpanelProps.SECTION_NAME]: EMPTY_STATE_SECTION_NAME,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: 'Limpar filtros',
		[MixpanelProps.COMMERCIAL_ID]: ''
	});

	skipNextFilterReset.value = true;
	clearFilters();
	if (clearFiltersSkipTimeout) clearTimeout(clearFiltersSkipTimeout);
	clearFiltersSkipTimeout = setTimeout(() => {
		skipNextFilterReset.value = false;
		clearFiltersSkipTimeout = null;
	}, 250);
	void nextTick(() => reloadVideos());
}

function handleViewProperties(): void {
	const resolvedStates = resolveShortsLocationStatesForLabels(
		currentFilters.value.macroregions ?? [],
		currentFilters.value.states ?? []
	);

	const path = PropertyFiltersPresenter.getPathFromFilters({
		transactionType: 'sell',
		...currentFilters.value,
		states: resolvedStates
	});

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Shorts',
		[MixpanelProps.SECTION_NAME]: EMPTY_STATE_SECTION_NAME,
		[MixpanelProps.DESTINATION_URL]: path,
		[MixpanelProps.CTA_TEXT]: 'Ver imóveis',
		[MixpanelProps.COMMERCIAL_ID]: ''
	});

	void navigateTo(path);
}

const isSidebarPreviewFetching = ref(false);

async function previewSidebarCount(
	previewFilters: ShortsFilters
): Promise<void> {
	isSidebarPreviewFetching.value = true;

	try {
		const mainFilters = { ...feedSession.filters.value };

		if (previewFilters.cities) {
			delete mainFilters.regions;
			delete mainFilters.macroregions;
		}

		const filtersWithLocation = {
			...mainFilters,
			...removeEmptyValues(previewFilters)
		};

		const result = await fetchVideos(
			withImagesEveryForced(filtersWithLocation),
			1
		);

		previewTotalAvailable.value =
			result.meta.totalAvailable ?? result.items.length;
	} finally {
		isSidebarPreviewFetching.value = false;
	}
}

watch(
	() => isLoaded.value && !videos.value.length,
	(shouldShowEmptyState: boolean) => {
		if (shouldShowEmptyState) {
			trackShortsEmptyState({
				filters: currentFilters.value,
				currentPage: route.fullPath,
				propertiesCount: totalAvailable.value
			});
		}
	}
);

onBeforeUnmount(() => {
	if (clearFiltersSkipTimeout) {
		clearTimeout(clearFiltersSkipTimeout);
		clearFiltersSkipTimeout = null;
	}
	unwatchFilters();
});
</script>

<template>
	<div class="relative flex min-h-screen flex-col bg-black">
		<ShortsFiltersBar
			:count-properties="totalAvailable"
			:is-fetching-properties="isLoading || isSidebarPreviewFetching"
			@preview-filters="previewSidebarCount"
		/>

		<div
			:class="
				cn(
					'relative z-0 w-full flex-1 shrink-0 bg-black pt-0',
					shortsListingPaddingX
				)
			"
		>
			<PageSkeleton v-if="!isLoaded && isLoading" />

			<template v-else>
				<PageEmptyState
					v-if="isLoaded && !videos.length"
					@clear-filters="handleClearFilters"
					@view-properties="handleViewProperties"
				/>

				<TransitionGroup
					v-else
					tag="div"
					enter-active-class="transition-opacity duration-500 ease-in-out"
					leave-active-class="transition-opacity duration-500 ease-in-out"
					enter-from-class="opacity-0"
					leave-to-class="opacity-0"
					class="grid grid-cols-2 gap-[2px] md:grid-cols-3 lg:grid-cols-4"
				>
					<VideoCardHomeFeed
						v-for="(item, index) in videos"
						:key="item.property.commercialId"
						:property="item.property"
						:thumbnail-url="item.thumbnailUrl"
						:video-url="item.videoUrl"
						:index="index"
						section-name="home feed shorts"
						card-type="shorts card"
						:img-eager-count="imgEagerCount"
						:img-preload-count="imgPreloadCount"
						:player-root-margin="playerRootMargin"
						:prefetch-variants="item.prefetchVariants"
						@click="() => handleVideoCardClick(item.property.commercialId)"
						@engaged="handleVideoCardEngaged(index)"
						@mouseenter="handleVideoCardHoverStart(index)"
						@mouseleave="handleVideoCardHoverEnd"
					/>
				</TransitionGroup>

				<div v-if="isInfiniteLoading" class="mt-0.5 [overflow-anchor:none]">
					<PageSkeleton />
				</div>

				<div
					ref="loadMoreRef"
					class="h-20 w-full opacity-0 [overflow-anchor:none]"
				/>
			</template>
		</div>
	</div>
</template>
